import './ChipBox.css'
import { useCallback, type ReactNode } from 'react'
import { InputFrame, type InputFrameProps } from '../layout/InputFrame'
import { cn } from '@/utilities/misc'
import { IconButton } from '../action/IconButton'

//================================================================================

export type ChipBoxProps<T> = InputFrameProps & {
  renderChip?: (chip: T) => ReactNode
  value: T[]
  setValue?: (value: T[]) => void
  onRemove?: (chip: T) => void
  children?: ReactNode
}

export function ChipBox<T>({
  value,
  setValue,
  renderChip,
  onRemove,
  children,
  ...inputFrameProps
}: ChipBoxProps<T>) {

  const canRemove = onRemove != null || setValue != null

  const className = cn(
    'ChipBox',
    inputFrameProps.className,
  )

  //--------------------------------------------------------------------------------
  const handleRemove = useCallback((chip: T) => {
    if (onRemove) {
      onRemove(chip)
      return
    }
    setValue?.(value.filter(c => c !== chip))
  }, [setValue, value])

  //--------------------------------------------------------------------------------
  return (
    <InputFrame
      {...inputFrameProps}
      className={className}
    >
      <div className='BorderedInput'>
        {value.length > 0 && (
          <div className='Chips'>
            {value.map((chip, index) => (
              <Chip
                key={index}
                chip={chip}
                canRemove={canRemove}
                renderChip={renderChip}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}
        <div className='AddChip'>
          {children}
        </div>
      </div>
    </InputFrame>
  )
}

//================================================================================

type ChipProps<T> = {
  chip: T
  canRemove?: boolean
  renderChip?: (chip: T) => ReactNode
  onRemove?: (chip: T) => void
}

function Chip<T>({
  chip,
  canRemove,
  renderChip,
  onRemove,
}: ChipProps<T>) {
  return (
    <div className='Chip'>
      {renderChip ? renderChip(chip) : String(chip)}
      {canRemove && (
        <IconButton
          icon='times'
          className='Remove'
          tooltip='Remove'
          onClick={() => onRemove?.(chip)}
        />
      )}
    </div>
  )
}
